import type { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'
import type {
  EmailAuditEvidence,
  EmailAuditFinding,
  EmailAuditSummary,
  EmailAuditTarget,
} from '@/lib/email/audit/contracts'
import { EMAIL_AUDIT_MAX_LINKS } from '@/lib/email/audit/limits'
import { FREE_TOOL_REPORT_TTL_DAYS } from '@/lib/free-tools/catalog'
import {
  FREE_EMAIL_AUDIT_ADDRESS,
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES,
  type FreeEmailAuditErrorCode,
} from '@/lib/free-tools/email-audit/contracts'
import { PREVIEW_CLIENTS } from '@/lib/mailgun/inspect/preview-clients'

/**
 * Every line of copy on `/email-audit` and its report pages, worded from the
 * `freeEmailAudit` catalog in the reader's language. The page and its
 * markdown twin read it with `getTranslations`, the client island with
 * `useEmailAuditCopy`, and the reply email (which knows no reader language)
 * in English (`lib/free-tools/email-audit/english-copy.ts`).
 */

/** `useTranslations('freeEmailAudit')`, or `getTranslations` on the server. */
type FreeEmailAuditTranslator = ReturnType<
  typeof useTranslations<'freeEmailAudit'>
>

const MAX_HTML_MB = Math.round(FREE_EMAIL_AUDIT_MAX_HTML_BYTES / 1024 / 1024)

/** How many inboxes the signed-in rendering preview covers. */
const RENDERING_CLIENT_COUNT = PREVIEW_CLIENTS.length

export const EMAIL_AUDIT_TAB_IDS = ['paste', 'upload', 'email'] as const

export type EmailAuditTabId = (typeof EMAIL_AUDIT_TAB_IDS)[number]

/** One line per check while the audit runs, in the order they settle. */
const PROGRESS_STEP_IDS = [
  'rules',
  'links',
  'images',
  'clients',
  'copy',
  'accessibility',
] as const

const WAY_STEP_IDS = ['paste', 'upload', 'email'] as const

const FAQ_IDS = [
  'free',
  'stored',
  'links',
  'score',
  'forward',
  'rendering',
] as const

type Category = EmailAuditFinding['category']

/** The finding categories, in the order every surface lists them. */
export const EMAIL_AUDIT_CATEGORY_IDS: ReadonlyArray<Category> = [
  'links',
  'images',
  'compliance',
  'accessibility',
  'compatibility',
  'copy',
  'size',
  'markup',
]

/** The free report's verdict, read with the score beside it. */
export type FreeEmailAuditVerdict = 'ready' | 'needs_review' | 'not_ready'

/** A caveat a free report states beside its score (`report-notes.ts`). */
export type FreeEmailAuditReportNote =
  | 'quickMode'
  | 'partialScore'
  | 'forwarded'

type EmailAuditFaqItem = { id: string; question: string; answer: string }

const MAX_NAMED_CLIENTS = 4

/** How a heading's `<accent>` words render (`pageHeroAccent` on the page). */
type AccentRenderer = (chunks: ReactNode) => ReactNode

/** The heading's words alone, for the markdown twin. */
const plainAccent = (chunks: string) => chunks

function withUrl(label: string, url: string): string {
  return url.trim() ? `${label} · ${url}` : label
}

export function emailAuditCopy(t: FreeEmailAuditTranslator) {
  const days = FREE_TOOL_REPORT_TTL_DAYS
  const count = RENDERING_CLIENT_COUNT
  const address = FREE_EMAIL_AUDIT_ADDRESS
  const faqAnswer = (id: (typeof FAQ_IDS)[number]) => {
    if (id === 'stored') {
      return t('faq.items.stored.answer', { days })
    }
    if (id === 'rendering') {
      return t('faq.items.rendering.answer', { count })
    }
    return t(`faq.items.${id}.answer`)
  }
  return {
    hero: {
      /** The heading with its `<accent>` words, wherever the language puts them. */
      title: (accent: AccentRenderer) => t.rich('hero.title', { accent }),
      titleText: t.markup('hero.title', { accent: plainAccent }),
      description: t('hero.description'),
    },
    tabsLabel: t('tabs.label'),
    tabs: EMAIL_AUDIT_TAB_IDS.map((id) => ({ id, label: t(`tabs.${id}`) })),
    form: {
      htmlLabel: t('form.htmlLabel'),
      htmlPlaceholder: t('form.htmlPlaceholder', {
        openTag: '<html>',
        closeTag: '</html>',
      }),
      subjectLabel: t('form.subjectLabel'),
      subjectPlaceholder: t('form.subjectPlaceholder'),
      subjectSummary: (subject: string) =>
        t('form.subjectSummary', { subject }),
      previewLabel: t('form.previewLabel'),
      previewPlaceholder: t('form.previewPlaceholder'),
      sample: t('form.sample'),
      sampleShort: t('form.sampleShort'),
      envelopeToggle: t('form.envelopeToggle'),
      submit: t('form.submit'),
      submitting: t('form.submitting'),
      sizeHint: t('form.sizeHint', { maxMb: MAX_HTML_MB }),
      sizeUsed: (used: string, max: string) =>
        t('form.sizeUsed', { used, max }),
      dropTitle: t('form.dropTitle'),
      dropBrowse: t('form.dropBrowse'),
      dropReplace: t('form.dropReplace'),
      emptyHtml: t('form.emptyHtml'),
      fileRejected: t('form.fileRejected'),
      fileTooLarge: t('form.fileTooLarge', { maxMb: MAX_HTML_MB }),
      fileUnreadable: t('form.fileUnreadable'),
      sampleFailed: t('form.sampleFailed'),
    },
    byEmail: {
      address,
      copy: t('byEmail.copy'),
      copied: t('byEmail.copied'),
      steps: [t('byEmail.steps.send'), t('byEmail.steps.reply')],
      note: t('byEmail.note'),
    },
    progress: {
      label: t('progress.label'),
      steps: PROGRESS_STEP_IDS.map((id) => t(`progress.steps.${id}`)),
    },
    verdict: (verdict: FreeEmailAuditVerdict) => t(`verdict.${verdict}`),
    error: (code: FreeEmailAuditErrorCode | 'network' | 'unreadable') =>
      code === 'too_large'
        ? t('errors.too_large', { maxMb: MAX_HTML_MB })
        : t(`errors.${code}`),
    report: {
      score: (score: number) => t('report.score', { score }),
      fullMode: t('report.fullMode'),
      quickMode: t('report.quickMode'),
      note: (note: FreeEmailAuditReportNote) => t(`report.notes.${note}`),
      prompt: t('report.prompt'),
      promptCopied: t('report.promptCopied'),
      promptCleanEmail: t('report.promptCleanEmail'),
      findingsTitle: t('report.findingsTitle'),
      railTitle: (fixes: number) => t('report.railTitle', { count: fixes }),
      railHint: t('report.railHint'),
      passedTitle: t('report.passedTitle'),
      uncheckedTitle: t('report.uncheckedTitle'),
      dock: (fixes: number) => t('report.dock', { count: fixes }),
      dockClean: t('report.dockClean'),
      noFindings: t('report.noFindings'),
      noFindingsUnverified: t('report.noFindingsUnverified'),
      findingsTruncatedNote: (total: number) =>
        t('report.findingsTruncatedNote', { total }),
      occurrences: (places: number) =>
        t('report.occurrences', { count: places }),
      share: t('report.share'),
      shared: t('report.shared'),
      again: t('report.again'),
      expires: (date: string) => t('report.expires', { date }),
    },
    preview: {
      title: t('preview.title'),
      widthLabel: t('preview.widthLabel'),
      desktop: t('preview.desktop'),
      mobile: t('preview.mobile'),
      frameTitle: t('preview.frameTitle'),
      pin: (number: number) => t('preview.pin', { number }),
    },
    category: (category: Category) => ({
      label: t(`categories.${category}.label`),
      description:
        category === 'links'
          ? t('categories.links.description', {
              maxLinks: EMAIL_AUDIT_MAX_LINKS,
            })
          : t(`categories.${category}.description`),
    }),
    severity: (severity: EmailAuditFinding['severity']) =>
      t(`severity.${severity}`),
    issueCount: (
      summary: Pick<EmailAuditSummary, 'blockers' | 'errors' | 'warnings'>
    ) => {
      const counts = [
        summary.blockers > 0
          ? t('counts.critical', { count: summary.blockers })
          : null,
        summary.errors > 0
          ? t('counts.errors', { count: summary.errors })
          : null,
        summary.warnings > 0
          ? t('counts.warnings', { count: summary.warnings })
          : null,
      ].filter((value): value is string => value !== null)
      return counts.length > 0 ? counts.join(' · ') : t('counts.none')
    },
    /** One short line for what backs a finding, or `null` when nothing does. */
    evidence: (evidence: EmailAuditEvidence | undefined): string | null => {
      if (!evidence) {
        return null
      }
      if (evidence.kind === 'http') {
        if (evidence.failure) {
          return t(`evidence.${evidence.failure}`)
        }
        return evidence.status === null
          ? t('evidence.noResponse')
          : t('evidence.http', { status: evidence.status })
      }
      if (evidence.kind === 'clients') {
        const named = evidence.unsupported.slice(0, MAX_NAMED_CLIENTS)
        const rest = evidence.unsupported.length - named.length
        const clients = named.join(', ')
        return rest > 0
          ? t('evidence.clientsMore', { clients, rest })
          : t('evidence.clients', { clients })
      }
      return t('evidence.judged')
    },
    /** Where in the email a finding points, or `null` for the whole email. */
    target: (target: EmailAuditTarget): string | null => {
      switch (target.kind) {
        case 'email':
          return null
        case 'subject':
          return t('target.subject')
        case 'preview_text':
          return t('target.preview_text')
        case 'link':
          return withUrl(
            t('target.link', { number: target.index + 1 }),
            target.displayUrl
          )
        case 'image':
          return withUrl(
            t('target.image', { number: target.index + 1 }),
            target.displayUrl
          )
        default:
          return target.selector
      }
    },
    sharedReport: {
      metaTitle: t('sharedReport.metaTitle'),
      title: (accent: AccentRenderer) =>
        t.rich('sharedReport.title', { accent }),
      description: t('sharedReport.description'),
      cta: t('sharedReport.cta'),
      missingTitle: t('sharedReport.missingTitle'),
      missingBody: t('sharedReport.missingBody', { days }),
      unavailableTitle: t('sharedReport.unavailableTitle'),
      unavailableBody: t('sharedReport.unavailableBody'),
    },
    /** The signed-in step the page sells: rendering across real inboxes. */
    rendering: {
      title: t('rendering.title'),
      description: t('rendering.description', { count }),
      lockedTitle: t('rendering.lockedTitle', { count }),
      lockedBody: t('rendering.lockedBody'),
      ctaWithEmail: t('rendering.ctaWithEmail'),
      ctaWithoutEmail: t('rendering.ctaWithoutEmail'),
      /** The report's compact pair under the copy-prompt button. */
      reportPreview: t('rendering.reportPreview', { count }),
      reportFix: t('rendering.reportFix'),
      /** The prompt the email arrives with in Brew's chat after sign-up. */
      renderingPrompt: t('rendering.renderingPrompt'),
      fixPrompt: t('rendering.fixPrompt'),
      blockedBody: t('rendering.blockedBody'),
      blockedDownload: t('rendering.blockedDownload'),
      blockedContinue: t('rendering.blockedContinue'),
    },
    checksSection: {
      title: t('checksSection.title'),
      description: t('checksSection.description'),
    },
    waysSection: {
      title: t('waysSection.title'),
      description: t('waysSection.description'),
      steps: WAY_STEP_IDS.map((id, index) => ({
        step: String(index + 1).padStart(2, '0'),
        title: t(`waysSection.steps.${id}.title`),
        body:
          id === 'email'
            ? t('waysSection.steps.email.body', { address })
            : t(`waysSection.steps.${id}.body`),
      })),
      addressLine: t('waysSection.addressLine', { address }),
    },
    faqTitle: t('faq.title'),
    faq: FAQ_IDS.map(
      (id): EmailAuditFaqItem => ({
        id,
        question: t(`faq.items.${id}.question`),
        answer: faqAnswer(id),
      })
    ),
  }
}

export type EmailAuditCopy = ReturnType<typeof emailAuditCopy>
